import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import Hls from "hls.js"

export default class extends Controller {
  static targets = ["banner", "offline", "player", "offlineMessage", "video"]

  connect() {
    this.isLive = !this.hasBannerTarget || !this.bannerTarget.classList.contains("hidden")
    this.heartbeatTimer = null
    this.hls = null

    this.channel = createConsumer().subscriptions.create("StreamStatusChannel", {
      received: (data) => {
        this.updateStatus(data.is_live)
      }
    })

    if (this.isLive) {
      this.startHeartbeat()
      this.initPlayer()
    }
  }

  disconnect() {
    this.stopHeartbeat()
    this.destroyPlayer()
    if (this.channel) {
      this.channel.unsubscribe()
    }
  }

  updateStatus(isLive) {
    this.isLive = isLive

    if (this.hasBannerTarget) {
      this.bannerTarget.classList.toggle("hidden", !isLive)
    }
    if (this.hasOfflineTarget) {
      this.offlineTarget.classList.toggle("hidden", isLive)
    }
    if (this.hasPlayerTarget) {
      this.playerTarget.classList.toggle("hidden", !isLive)
    }
    if (this.hasOfflineMessageTarget) {
      this.offlineMessageTarget.classList.toggle("hidden", isLive)
    }

    if (isLive) {
      this.startHeartbeat()
      // The player markup is server-rendered but hidden while offline; once we
      // un-hide it, (re)initialize hls.js and start playback for viewers who
      // were already waiting on the page when the stream came online.
      this.initPlayer()
    } else {
      this.stopHeartbeat()
      this.destroyPlayer()
    }
  }

  // Player lifecycle. hls.js is bundled with the app (not loaded from a CDN),
  // so playback can't be broken by an ad-blocker or network that filters
  // third-party script hosts. Safari/iOS still use their native HLS support.
  initPlayer() {
    if (!this.hasVideoTarget) return
    const video = this.videoTarget
    const src = video.dataset.streamUrl
    if (!src) return

    if (Hls.isSupported()) {
      // Guard against double-init (connect + turbo:load + live toggle).
      this.destroyPlayer()
      this.hls = new Hls()
      this.hls.loadSource(src)
      this.hls.attachMedia(video)
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => this.attemptPlay(video))
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Native HLS (Safari/iOS) — start once metadata is ready.
      if (video.src !== src) { video.src = src }
      video.addEventListener("loadedmetadata", () => this.attemptPlay(video), { once: true })
    }
  }

  destroyPlayer() {
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }
  }

  // Try to play with sound; if the browser's autoplay policy blocks it, fall
  // back to muted autoplay (which is always permitted).
  attemptPlay(video) {
    const p = video.play()
    if (p && p.catch) {
      p.catch(() => {
        video.muted = true
        video.play().catch(() => {})
      })
    }
  }

  startHeartbeat() {
    if (this.heartbeatTimer) return
    this.heartbeatTimer = setInterval(() => {
      if (this.channel) {
        this.channel.perform("heartbeat")
      }
    }, 30000)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }
}
